import clsx from 'clsx';
import React, {useState} from 'react';
import TextareaAutosize from 'react-textarea-autosize';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import {Body, Button, Headline} from '@lightricks/react-design-system';
import raiseFlashMessage from '@/utils/raiseFlashMessage';
import translate from '@/utils/translate';
import {CampaignApprovalStep} from '@/types/campaign';
import {
  ContentSubmission,
  ContentSubmissionVersion,
} from '@/types/contentSubmission';
import {getApprovalStepDisplayName} from '@/views/campaigns/constants/approvalSteps';
import Checkbox from '@/components/checkbox';
import Modal from '@/components/modal';
import NotificationBanner, {
  NotificationBannerState,
  NotificationContentType,
} from '@/components/notification-banner';
import useCreateContentSubmissionApprovalsMutation from '@/hooks/mutations/use-create-content-submission-approvals';
import styles from './ApproveContentSubmissionModal.module.scss';

export interface ApproveContentSubmissionModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (brandMessage?: string) => void;
  contentSubmission: ContentSubmission;
  version: ContentSubmissionVersion;
  campaignApprovalSteps: CampaignApprovalStep[];
  onDepartmentToggle?: (step: string, selected: boolean) => void;
  onSubmit?: () => void;
}

function ApproveContentSubmissionModal({
  isOpen,
  onClose,
  onConfirm,
  contentSubmission,
  version,
  campaignApprovalSteps,
  onDepartmentToggle,
  onSubmit,
}: ApproveContentSubmissionModalProps) {
  const [selectedSteps, setSelectedSteps] = useState<string[]>([]);
  const [message, setMessage] = useState('');

  const requiredStepsNames = campaignApprovalSteps.map((s) => s.step);
  const approvedStepsNames = (version.approvals ?? []).map((a) => a.step);
  const approvedCount = approvedStepsNames.length;
  const totalCount = requiredStepsNames.length;

  const isContentApproval = approvedCount === totalCount;

  const {mutate: createApprovals, isLoading: isCreatingApprovals} =
    useCreateContentSubmissionApprovalsMutation();

  const handleStepToggle = (step: string) => {
    const isCurrentlySelected = selectedSteps.includes(step);
    onDepartmentToggle?.(step, !isCurrentlySelected);
    setSelectedSteps((prev) =>
      isCurrentlySelected ? prev.filter((s) => s !== step) : [...prev, step]
    );
  };

  const handleClose = () => {
    setSelectedSteps([]);
    setMessage('');
    onClose();
  };

  const handleSubmit = () => {
    onSubmit?.();
    if (isContentApproval) {
      onConfirm(message || undefined);
      handleClose();
    } else {
      createApprovals(
        {
          contentSubmissionId: contentSubmission.id,
          contentSubmissionVersionId: version.id,
          steps: selectedSteps,
        },
        {
          onSuccess: () => {
            const willBeAllApproved =
              approvedCount + selectedSteps.length === totalCount;
            if (!willBeAllApproved) {
              handleClose();
            }
          },
          onError: () => {
            raiseFlashMessage({
              status: 'error',
              message: translate('Failed to approve. Please try again.'),
            });
          },
        }
      );
    }
  };

  const submitLabel =
    isContentApproval && totalCount > 0
      ? translate('Complete approval')
      : translate('Approve');

  const isSubmitDisabled =
    isCreatingApprovals || (!isContentApproval && selectedSteps.length === 0);

  return (
    <Modal
      open={isOpen}
      onClose={handleClose}
      size="small"
      contentClassName={styles.modalContent}
    >
      <Stack direction="column" className={styles.content}>
        <Headline size="md" className={styles.title}>
          {translate('Approve asset')}
        </Headline>
        {totalCount > 0 ? (
          <Body size="md" className={styles.subtitle}>
            {translate('departments-approved-count', {
              approvedCount,
              totalCount,
            })}
          </Body>
        ) : null}

        {totalCount > 0 ? (
          <Stack direction="column" className={styles.stepsList}>
            {requiredStepsNames.map((step) => {
              const isApproved = approvedStepsNames.includes(step);
              const isSelected = selectedSteps.includes(step);
              const isChecked = isApproved || isSelected;
              const isDisabled = isApproved || isContentApproval;

              return (
                <Stack
                  direction="row"
                  key={step}
                  className={clsx(
                    styles.stepRow,
                    isDisabled && styles.stepRowDisabled
                  )}
                >
                  <Checkbox
                    checked={isChecked}
                    onChange={
                      isDisabled ? () => {} : () => handleStepToggle(step)
                    }
                    shouldStopPropagation={false}
                  />
                  <Body size="md" className={styles.stepName}>
                    {getApprovalStepDisplayName(step)}
                  </Body>
                  <Body
                    size="sm"
                    className={
                      isApproved ? styles.approvedBadge : styles.awaitingBadge
                    }
                  >
                    {isApproved ? translate('Approved') : translate('Awaiting')}
                  </Body>
                </Stack>
              );
            })}
          </Stack>
        ) : null}

        {isContentApproval ? (
          <Stack direction="column" className={styles.messageSection}>
            <Body size="sm" className={styles.messageLabel}>
              {translate('Message to creator (optional)')}
            </Body>
            <TextareaAutosize
              className={styles.textarea}
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              placeholder={translate('approve-asset-message-placeholder')}
              minRows={3}
              maxRows={5}
            />
          </Stack>
        ) : null}

        {isContentApproval && totalCount > 0 ? (
          <Box sx={{marginBottom: '16px'}}>
            <NotificationBanner
              state={NotificationBannerState.SUCCESS}
              content={{
                type: NotificationContentType.TITLE_AND_DESCRIPTION,
                title: translate('Final Approval'),
                description: translate('approve-asset-final-approval-alert'),
              }}
            />
          </Box>
        ) : null}

        <Stack direction="row" className={styles.actions}>
          <Button
            appearance="neutral"
            mode="tinted"
            size="large"
            onClick={handleClose}
            disabled={isCreatingApprovals}
          >
            {translate('Cancel')}
          </Button>
          <Button
            appearance="neutral"
            mode="filled"
            size="large"
            onClick={handleSubmit}
            disabled={isSubmitDisabled}
          >
            {submitLabel}
          </Button>
        </Stack>
      </Stack>
    </Modal>
  );
}

export default ApproveContentSubmissionModal;
